import { Component, EventEmitter, HostBinding, HostListener, Input, OnInit, Output, OnDestroy } from '@angular/core';
import * as moment from 'moment';
import { PickingStatusEnum } from '../picking.component';
import { CrateStateEnum } from 'src/models/Crate';
import { V3ProductsDataSourceService } from 'src/app/core/data-sources/v3/products/v3-products-data-source.service';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-picking-received-out-order-item',
  templateUrl: './picking-received-out-order-item.component.html',
  styleUrls: ['./picking-received-out-order-item.component.scss']
})
export class PickingReceivedOutOrderItemComponent implements OnInit, OnDestroy {
  @Input() item;
  @Input() disabled = false;

  @Input() showToolTip = false;
  @Input() toolTipMessage = '';

  @HostBinding('class.isOdd') @Input() isOdd;

  @Output() itemClick = new EventEmitter();
  @Output() processClick = new EventEmitter();

  date;
  checkingAvailability = false;
  isAvailable: boolean = null;
  private destroy$ = new Subject();
  private static skuQuantityCache = new Map<string, Promise<number>>();

  constructor(
    private v3ProductsService: V3ProductsDataSourceService
  ) { }

  ngOnInit(): void {
    this.date = moment(this.item?.issuedAt).format('DD/MM/YYYY - HH:mm');

    timer(500).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadAvailabilityForProducts();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadAvailabilityForProducts(): Promise<void> {
    if (this.checkingAvailability) {
      return;
    }

    if (!this.item?.products || this.item.products.length === 0) {
      return;
    }

    const productSkus = this.item.products.map(product => product?.sku);

    this.checkingAvailability = true;

    try {
      const cache = PickingReceivedOutOrderItemComponent.skuQuantityCache;
      const uncachedSkus = productSkus.filter(sku => !cache.has(sku));

      if (uncachedSkus.length > 0) {
        const palletStates = [CrateStateEnum.STORED];
        const params = { skus: uncachedSkus, palletStates };
        const groundPositionType = ['STOCK', 'PICKING', 'PICKING_MISCELLANEOUS'];
        const fetchPromise = this.v3ProductsService.getV3ProductsCount(groundPositionType, params)
          .then(products => products || [])
          .catch(() => []);

        for (const sku of uncachedSkus) {
          cache.set(sku, fetchPromise.then(products => {
            const found = products.find(p => p.sku === sku);
            return found?.quantity || 0;
          }));
        }
      }

      let allAvailable = true;

      for (const itemProduct of this.item.products) {
        const availableQty = await (cache.get(itemProduct.sku) ?? Promise.resolve(0));

        if (availableQty < itemProduct.quantity) {
          allAvailable = false;
        }
      }

      this.isAvailable = allAvailable;
    } catch (error) {
      this.isAvailable = false;
    } finally {
      this.checkingAvailability = false;
    }
  }

  @HostListener('click') onClick(){
    if(!this.disabled){
      this.itemClick.emit(this.item);
    }
  }

  translateStatus(status) {
    switch (status) {
      case PickingStatusEnum.UNAVAILABLE_PRODUCTS:
        return 'Produto indisponível';
      case PickingStatusEnum.INVALID:
        return 'Inválido';
      case PickingStatusEnum.NOT_ENOUGH_ON_STOCK:
        return 'Quantidade não suficiente em estoque';
      case PickingStatusEnum.OK:
        return 'Ordem recebida';
      case PickingStatusEnum.UNKNOWN:
        return '--';
      default:
        return status || '---';
    }
  }

  showTooltip() {
    if (this.showToolTip) {
      return this.toolTipMessage;
    }
    return null;
  }

  async processClickHandler($event) {
    this.processClick.emit(this.item);
    $event.stopPropagation();
  }
}
